import React from 'react';
import { CostBreakdown as CostBreakdownType, BoxSpecifications } from '../types';
import { formatPKR, formatUSD, formatWeight } from '../utils/formatting';
import { BarChart3, Target, Scale, TrendingUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { useAuth } from '../hooks/useAuth';
import { formatDimension, formatDimensions, formatOpenDimensions } from '../utils/unitConversion';

// Helper function to format material name
const formatMaterialName = (material: string, corrugatedType?: string): string => {
  if (material === 'corrugated' && corrugatedType) {
    const corrugatedFormatted = corrugatedType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return `Corrugated (${corrugatedFormatted})`;
  }
  return material.replace(/\b\w/g, l => l.toUpperCase());
};

interface Props {
  breakdown: CostBreakdownType;
  specs: BoxSpecifications;
  isOpen: boolean;
  onClose: () => void;
  inputMode: 'dimensions' | 'openSize';
  openDimensions?: { openLength: number; openWidth: number };
  profitPercentage?: number;
  // Current slider values for live calculations
  currentManufacturerProfit?: number;
  currentCompanyMargin?: number;
  // Multi-quantity selection (optional)
  quantityOptions?: number[];
  selectedQuantity?: number | null;
  onSelectQuantity?: (qty: number) => void;
  // Admin margin data for saved quotes
  adminMarginData?: {
    manufacturerMarginPercent?: number;
    companyMarginPercent?: number;
    adminCompanyProfitAmount?: number;
  };
  // Quote identification for saved quotes display
  isAutoSavedQuote?: boolean; // true for auto-saved drafts, false/undefined for manual saves
  savedQuoteData?: {
    baseManufacturerProfit?: number;
    baseManufacturerProfitAmount?: number;
    agentMarginPercent?: number;
    agentCompanyProfit?: number;
    manufacturerMarginPercent?: number;
    manufacturerProfitAmount?: number;
    companyMarginPercent?: number;
    adminCompanyProfitAmount?: number;
  };
}

export const CostBreakdownModal: React.FC<Props> = ({
  breakdown,
  specs,
  isOpen,
  onClose,
  inputMode,
  openDimensions,
  profitPercentage = 20,
  currentManufacturerProfit,
  currentCompanyMargin,
  quantityOptions,
  selectedQuantity,
  onSelectQuantity,
  adminMarginData,
  isAutoSavedQuote = false,
  savedQuoteData
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'company_admin';
  const isAgent = user?.role === 'agent' || user?.role === 'user';
  const canViewFullBreakdown = isAdmin || user?.can_view_breakdown === true;
  
  // Use backend-calculated productionSubtotal when available (it already includes material + wastage)
  // Only fall back to manual calculation if productionSubtotal is not provided
  const productionCost = (typeof breakdown.productionSubtotal === 'number')
    ? breakdown.productionSubtotal
    : ((breakdown.materialCost || 0)
      + (breakdown.sheetWastageCost || 0)
      + (breakdown.printingCost || 0)
      + (breakdown.scanningCost || 0)
      + (breakdown.platesCost || 0)
      + (breakdown.dieCost || 0)
      + (breakdown.dieCuttingCost || 0)
      + (breakdown.pastingCost || 0)
      + (breakdown.whiteUnderprintCost || 0)
      + (breakdown.laminationCost || 0)
      + (breakdown.laminationWastageCost || 0)
      + (breakdown.insideLaminationCost || 0)
      + (breakdown.insideLaminationWastageCost || 0)
      + (breakdown.additionalFeaturesCost || 0)
      // Insert costs (FIX: these were missing from fallback calculation)
      + (breakdown.insertMaterialCost || 0)
      + (breakdown.insertSheetWastageCost || 0)
      + (breakdown.insertPrintingCost || 0)
      + (breakdown.insertPlatesCost || 0)
      + (breakdown.insertLaminationCost || 0)
      + (breakdown.insertLaminationWastageCost || 0));

  // Base manufacturer profit fields (now always provided by backend)
  const baseManufacturerPercent = typeof breakdown.baseManufacturerProfit === 'number'
    ? breakdown.baseManufacturerProfit
    : undefined;
  const baseManufacturerAmount = typeof breakdown.baseManufacturerProfitAmount === 'number'
    ? breakdown.baseManufacturerProfitAmount
    : undefined;

  // Removed excessive debug logging

  // Determine effective profits based on quote type
  const getEffectiveProfits = () => {
    if (isAutoSavedQuote && savedQuoteData) {
      // Auto-saved quote: use base manufacturer profit only, no company margin
      return {
        manufacturerProfit: savedQuoteData.baseManufacturerProfit ?? baseManufacturerPercent ?? 20,
        manufacturerAmount: savedQuoteData.baseManufacturerProfitAmount ?? 0,
        companyMarginPercent: 0, // No company margin for auto-saved quotes
        companyMarginAmount: 0,
        agentMargin: savedQuoteData.agentMarginPercent ?? 0
      };
    } else if (savedQuoteData) {
      // Manual saved quote: use whatever admin chose
      // For multi-quantity quotes, prioritize breakdown-level data
      const breakdownExtended = breakdown as CostBreakdownType & {companyMarginPercent?: number; adminCompanyProfitAmount?: number; vendorProfit?: number};
      const savedCompanyMarginPercent = breakdownExtended.companyMarginPercent ?? savedQuoteData.companyMarginPercent ?? (adminMarginData?.companyMarginPercent ?? 0);
      const savedCompanyMarginAmount = breakdownExtended.adminCompanyProfitAmount ?? savedQuoteData.adminCompanyProfitAmount ?? adminMarginData?.adminCompanyProfitAmount;

      // Calculate manufacturer profit amount dynamically for each variant
      // Priority: baseManufacturerProfit (admin setting) > vendorProfit (legacy) > savedQuoteData > default
      const savedManufacturerProfit = baseManufacturerPercent ?? breakdownExtended.vendorProfit ?? savedQuoteData.manufacturerMarginPercent ?? 20;
      const calculatedManufacturerAmount = (productionCost * savedManufacturerProfit) / 100;

      // Calculate company margin amount dynamically for each variant
      let calculatedCompanyMarginAmount = 0;
      if (savedCompanyMarginPercent > 0) {
        // Always recalculate based on current breakdown's production cost and CALCULATED manufacturer profit
        const baseTotal = productionCost + calculatedManufacturerAmount + shippingCost;
        calculatedCompanyMarginAmount = (baseTotal * savedCompanyMarginPercent) / 100;

        // Debug logging for multi-quantity company profit
        console.log('🔍 CostBreakdownModal Debug - Multi-quantity Company Profit:');
        console.log('- Current productionCost:', productionCost);
        console.log('- Current shippingCost:', shippingCost);
        console.log('- calculatedManufacturerAmount:', calculatedManufacturerAmount);
        console.log('- baseTotal:', baseTotal);
        console.log('- savedCompanyMarginPercent:', savedCompanyMarginPercent);
        console.log('- Calculated companyMarginAmount:', calculatedCompanyMarginAmount);
      }

      return {
        manufacturerProfit: savedManufacturerProfit,
        manufacturerAmount: calculatedManufacturerAmount,
        companyMarginPercent: savedCompanyMarginPercent,
        companyMarginAmount: calculatedCompanyMarginAmount,
        agentMargin: savedQuoteData.agentMarginPercent ?? 0
      };
    } else {
      // Live calculation: prefer breakdown values (set from parent), otherwise use slider values or defaults
      const currentAgentMargin = isAgent ? profitPercentage : 0;
      const liveManufacturerProfit = currentManufacturerProfit ?? baseManufacturerPercent ?? 20;
      const liveCompanyMargin = breakdown.companyProfit ?? currentCompanyMargin ?? adminMarginData?.companyMarginPercent ?? 0;

      // Calculate amounts based on current values
      const liveManufacturerAmount = (productionCost * liveManufacturerProfit) / 100;
      const baseTotal = productionCost + liveManufacturerAmount + shippingCost;
      const liveCompanyMarginAmount = breakdown.companyProfitAmount ?? ((baseTotal * liveCompanyMargin) / 100);

      return {
        manufacturerProfit: liveManufacturerProfit,
        manufacturerAmount: liveManufacturerAmount,
        companyMarginPercent: liveCompanyMargin,
        companyMarginAmount: liveCompanyMarginAmount,
        agentMargin: currentAgentMargin
      };
    }
  };

  // Build totals consistent with CostBreakdownCompact for agents:
  // baseTotalForAgent = (production + base manufacturer) + shipping
  const shippingCost = breakdown.shippingCost || 0;

  const effectiveProfits = getEffectiveProfits();
  const productionWithBaseManufacturer = productionCost + (baseManufacturerAmount || 0);
  const baseTotalForAgent = productionWithBaseManufacturer + shippingCost;
  // For agents, always mirror the controller (Cost Analysis) or saved percent via prop
  const agentMarginPercent = isAgent ? profitPercentage : 0;
  const agentCompanyProfit = isAgent ? (baseTotalForAgent * agentMarginPercent / 100) : 0;

  // displayTotal will be defined after finalTotal calculation
  const safeNumber = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const parsed = Number.parseFloat(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  };

  const normalizedBaseManufacturerPercent = safeNumber(baseManufacturerPercent);
  const normalizedBaseManufacturerAmount = safeNumber(baseManufacturerAmount);

  const vendorProfitPercent = safeNumber(breakdown.vendorProfit);
  const agentMarginPercentFromData = safeNumber(breakdown.agentMargin);

  let agentMarginPercentDisplay = agentMarginPercentFromData;
  if (agentMarginPercentDisplay == null) {
    if (isAgent) {
      agentMarginPercentDisplay = agentMarginPercent;
    } else if (vendorProfitPercent != null) {
      if (normalizedBaseManufacturerPercent != null) {
        if (Math.abs(vendorProfitPercent - normalizedBaseManufacturerPercent) > 0.0001) {
          agentMarginPercentDisplay = vendorProfitPercent;
        }
      } else {
        agentMarginPercentDisplay = vendorProfitPercent;
      }
    }
  }

  const vendorProfit = breakdown.vendorProfit ?? specs.vendorProfit ?? 20;
  const vendorProfitAmount = isAgent
    ? agentCompanyProfit
    : (isAdmin
        ? effectiveProfits.companyMarginAmount
        : currentVendorProfitAmount ?? ((typeof breakdown.vendorProfitAmount === 'number')
            ? breakdown.vendorProfitAmount
            : ((productionCost * (vendorProfit || 0)) / 100)));

  const agentMarginAmountFromData = safeNumber(breakdown.agentMarginAmount);

  const computedAgentMarginAmount = agentMarginPercentDisplay != null
    ? baseTotalForAgent * (agentMarginPercentDisplay / 100)
    : null;

  let agentMarginAmountDisplay = agentMarginAmountFromData;
  if (isAgent) {
    agentMarginAmountDisplay = agentCompanyProfit;
  } else if (agentMarginPercentDisplay != null) {
    if (vendorProfitAmount != null && vendorProfitPercent != null && Math.abs(vendorProfitPercent - agentMarginPercentDisplay) < 0.0001) {
      agentMarginAmountDisplay = computedAgentMarginAmount ?? vendorProfitAmount;
    } else {
      agentMarginAmountDisplay = computedAgentMarginAmount ?? agentMarginAmountFromData ?? vendorProfitAmount;
    }
  }

  // Use the open dimensions passed from parent (calculated by StepperForm)

  // Calculate boxes per plate based on plate size and open dimensions
  const calculateBoxesPerPlate = (plateSize: string, openLength: number, openWidth: number): number => {
    // Parse plate size (e.g., "18x25" -> width: 18, height: 25)
    const [width, height] = plateSize.split('x').map(n => parseFloat(n));
    if (!width || !height) return 0;
    
    // Try both orientations and pick the best fit
    const orientation1 = Math.floor(width / openLength) * Math.floor(height / openWidth);
    const orientation2 = Math.floor(width / openWidth) * Math.floor(height / openLength);
    return Math.max(orientation1, orientation2);
  };

  // Use the open dimensions passed from parent (always calculated by StepperForm)
  const openDims = openDimensions;
  
  // SIMPLIFIED 2-MARGIN SYSTEM: Unit cost calculation matching CostBreakdownCompact
  // Important: For agents, breakdown.total already includes base manufacturer profit

  let finalTotal, finalCostPerUnit, companyProfitAmount, currentVendorProfitAmount, subtotalWithVendorProfit;

  if (isAgent) {
    // For agents: Use the same calculation logic as CostBreakdownCompact
    // Calculate base total from individual components (without double-counting agent margin)
    const baseManufacturerAmount = (breakdown as CostBreakdownType & {baseManufacturerProfitAmount?: number}).baseManufacturerProfitAmount || ((productionCost * (baseManufacturerPercent || 20)) / 100);
    const baseTotal = productionCost + baseManufacturerAmount + shippingCost;
    // Use profitPercentage directly - this is the agent margin from the slider
    const currentAgentMargin = profitPercentage;
    companyProfitAmount = (baseTotal * currentAgentMargin) / 100;
    finalTotal = baseTotal + companyProfitAmount;
    finalCostPerUnit = finalTotal / specs.quantity;

    // For display purposes, calculate what the vendor profit amount would be
    const currentVendorProfit = effectiveProfits.manufacturerProfit;
    currentVendorProfitAmount = (productionCost * currentVendorProfit) / 100;
    subtotalWithVendorProfit = productionCost + currentVendorProfitAmount;
  } else {
    // For admins: Use the original calculation logic
    // Step 1: Apply vendor profit to production cost
    const currentVendorProfit = effectiveProfits.manufacturerProfit;
    currentVendorProfitAmount = (productionCost * currentVendorProfit) / 100;
    subtotalWithVendorProfit = productionCost + currentVendorProfitAmount;

    // Step 2: Apply company profit to (production + vendorProfit + shipping)
    const currentCompanyProfit = effectiveProfits.companyMarginPercent;
    const totalBeforeCompanyProfit = subtotalWithVendorProfit + shippingCost;
    companyProfitAmount = (totalBeforeCompanyProfit * currentCompanyProfit) / 100;
    finalTotal = totalBeforeCompanyProfit + companyProfitAmount;
    finalCostPerUnit = finalTotal / specs.quantity;
  }

  // Use simplified calculation results for display - matches CostBreakdownCompact logic
  const displayUnitCost = finalCostPerUnit;
  const displayTotal = finalTotal;

  // Use backend's calculated boxes per sheet value (which includes penalty system logic)
  const boxesPerPlate = breakdown.printingPlateData?.outsidePrinting?.boxesPerSheet 
    || breakdown.printingPlateData?.insidePrinting?.boxesPerSheet
    || (breakdown.selectedPlateSize && openDims 
        ? calculateBoxesPerPlate(breakdown.selectedPlateSize, openDims.openLength, openDims.openWidth)
        : null);

  const renderDetail = (label: string, value: string | number, isLast = false) => (
    <div className={`flex justify-between items-center py-1.5 text-sm ${!isLast ? 'border-b border-border' : ''}`}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="font-medium text-xs text-foreground">{value}</span>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] bg-background text-foreground p-0 border border-border">
        {/* Header */}
        <DialogHeader className="p-4 pr-12 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-muted border border-border">
              <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                <DialogTitle className="text-lg font-semibold text-foreground">Detailed Cost Analysis</DialogTitle>
              </div>
              <DialogDescription className="text-sm text-muted-foreground mt-0.5">
                {specs.quantity.toLocaleString()} {inputMode === 'openSize' ? 'open size' : specs.boxType.replace(/-/g, ' ')} boxes
              </DialogDescription>
            </div>
            {/* Quantity selector (optional) */}
            {quantityOptions && quantityOptions.length > 0 && onSelectQuantity && (
              <div className="ml-auto relative">
                <select
                  aria-label="Select quantity"
                  value={selectedQuantity ?? specs.quantity}
                  onChange={(e) => onSelectQuantity(parseInt(e.target.value))}
                  className="h-8 pl-2 pr-8 rounded-md border border-border bg-background text-foreground text-sm appearance-none"
                >
                  {quantityOptions.map((q) => (
                    <option key={q} value={q}>{q.toLocaleString()} units</option>
                  ))}
                </select>
                <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 12a1 1 0 01-.707-.293l-4-4a1 1 0 011.414-1.414L10 9.586l3.293-3.293a1 1 0 111.414 1.414l-4 4A1 1 0 0110 12z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
        </DialogHeader>
        
        <div className="overflow-y-auto p-4">
          {/* Permission Notice for Restricted Users */}
          {!canViewFullBreakdown && (
            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md">
              <div className="flex items-start gap-2">
                <span className="text-amber-600 dark:text-amber-400">ℹ️</span>
                <div className="text-sm text-amber-800 dark:text-amber-300">
                  <p className="font-medium mb-1">Limited Access</p>
                  <p className="text-xs">You have restricted access to cost breakdown details. Contact your company administrator to request full access permissions.</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Key Metrics */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="p-3 bg-muted rounded-md border border-border text-center">
              <Target className="h-3.5 w-3.5 mx-auto mb-1.5 text-muted-foreground" />
              <div className="text-xs font-medium text-muted-foreground mb-1">Unit Cost</div>
              <div className="text-sm font-semibold text-foreground">{formatUSD(displayUnitCost)}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {formatPKR(displayUnitCost)}
              </div>
            </div>
            <div className="p-3 bg-muted rounded-md border border-border text-center flex flex-col justify-center">
              <Scale className="h-3.5 w-3.5 mx-auto mb-1.5 text-muted-foreground" />
              <div className="text-xs font-medium text-muted-foreground mb-1">Weight</div>
              <div className="text-sm font-semibold text-foreground">{formatWeight(breakdown.weight)}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {formatMaterialName(specs.material, specs.corrugatedType)}
              </div>
            </div>
            <div className="p-3 bg-muted rounded-md border border-border text-center flex flex-col justify-center">
              <BarChart3 className="h-3.5 w-3.5 mx-auto mb-1.5 text-muted-foreground" />
              <div className="text-xs font-medium text-muted-foreground mb-1">Box Type</div>
              <div className="text-sm font-semibold text-foreground">{inputMode === 'openSize' ? 'Open Size' : specs.boxType.replace(/-/g, ' ')}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {specs.gsm} GSM
              </div>
            </div>
            <div className={`p-3 bg-muted rounded-md border border-border text-center ${inputMode === 'openSize' ? 'flex flex-col justify-center' : ''}`}>
              <TrendingUp className="h-3.5 w-3.5 mx-auto mb-1.5 text-muted-foreground" />
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {inputMode === 'openSize' ? 'Open Size' : 'Dimensions'}
              </div>
              <div className="text-sm font-semibold text-foreground">
                {inputMode === 'openSize' && openDimensions 
                  ? formatOpenDimensions(openDimensions.openLength, openDimensions.openWidth, specs.dimensionUnit || 'in')
                  : formatDimensions(specs.length, specs.width, specs.height, specs.dimensionUnit || 'in')
                }
              </div>
              {inputMode === 'dimensions' && openDimensions && (
                <div className="text-xs text-muted-foreground mt-1">
                  Open: {formatOpenDimensions(openDimensions.openLength, openDimensions.openWidth, specs.dimensionUnit || 'in')}
                </div>
              )}
            </div>
          </div>

          {/* Cost Breakdown Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="bg-muted rounded-md border border-border border-l-4 border-l-primary">
              <div className="p-3 pb-2">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-1 bg-primary rounded-full"></div>
                  <h3 className="text-sm font-medium text-foreground">Material & Printing</h3>
                </div>
              </div>
              <div className="px-3 pb-3 space-y-1">
                {canViewFullBreakdown ? (
                  <>
                    {renderDetail(
                      `Material Cost${breakdown.standardSheetSize ? ` (${breakdown.standardSheetSize.width}"×${breakdown.standardSheetSize.height}", ${breakdown.boxesPerSheet} ${breakdown.boxesPerSheet === 1 ? 'box' : 'boxes'})` : ''}`, 
                      formatPKR(isAdmin ? breakdown.materialCost : breakdown.materialCost + breakdown.sheetWastageCost)
                    )}
                    {isAdmin && renderDetail("Material Wastage", formatPKR(breakdown.sheetWastageCost))}
                    {breakdown.printingCost > 0 && renderDetail("Printing Cost", formatPKR(breakdown.printingCost))}
                    {breakdown.whiteUnderprintCost > 0 && renderDetail("White Underprint (Safaida)", formatPKR(breakdown.whiteUnderprintCost))}
                    {breakdown.platesCost > 0 && renderDetail(
                      `Plates Cost${breakdown.selectedPlateSize && breakdown.printingCost > 0 ? ` (${breakdown.selectedPlateSize}", ${boxesPerPlate || 0} ${(boxesPerPlate || 0) === 1 ? 'box' : 'boxes'})` : ''}`, 
                      formatPKR(breakdown.platesCost)
                    )}
                  </>
                ) : (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    Detailed breakdown not available for your account
                  </div>
                )}
                <div className="flex justify-between items-center py-1.5 text-sm font-medium border-t border-border mt-2 pt-2">
                  <span className="text-xs text-foreground/80">Material & Printing Total</span>
                  <span className="text-xs font-semibold text-foreground">{formatPKR(breakdown.materialCost + breakdown.sheetWastageCost + breakdown.printingCost + breakdown.scanningCost + breakdown.platesCost)}</span>
                </div>
              </div>
            </div>

            {/* Insert Section - show for ALL users if insert costs exist */}
            {breakdown.insertSubtotal && breakdown.insertSubtotal > 0 && (
              <div className="bg-muted rounded-md border border-border border-l-4 border-l-amber-500">
                <div className="p-3 pb-2">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-1 bg-amber-500 rounded-full"></div>
                    <h3 className="text-sm font-medium text-foreground">Insert Costs</h3>
                  </div>
                </div>
                <div className="px-3 pb-3 space-y-1">
                  {renderDetail("Insert Material", formatPKR((breakdown.insertMaterialCost || 0) + (breakdown.insertSheetWastageCost || 0)))}
                  {(breakdown.insertPrintingCost || 0) > 0 && renderDetail("Insert Printing", formatPKR(breakdown.insertPrintingCost || 0))}
                  {(breakdown.insertPlatesCost || 0) > 0 && renderDetail("Insert Plates", formatPKR(breakdown.insertPlatesCost || 0))}
                  {(breakdown.insertLaminationCost || 0) > 0 && renderDetail("Insert Lamination", formatPKR((breakdown.insertLaminationCost || 0) + (breakdown.insertLaminationWastageCost || 0)))}
                  <div className="flex justify-between items-center py-1.5 text-sm font-medium border-t border-border mt-2 pt-2">
                    <span className="text-xs text-foreground/80">Insert Total</span>
                    <span className="text-xs font-semibold text-amber-600">{formatPKR(breakdown.insertSubtotal || 0)}</span>
                  </div>
                </div>
              </div>
            )}

            {canViewFullBreakdown && (
              <div className="bg-muted rounded-md border border-border border-l-4 border-l-primary">
                <div className="p-3 pb-2">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-1 bg-primary rounded-full"></div>
                    <h3 className="text-sm font-medium text-foreground">Processing & Finishing</h3>
                  </div>
                </div>
                <div className="px-3 pb-3 space-y-1">
                  {renderDetail("Die Cost", formatPKR(breakdown.dieCost))}
                  {renderDetail("Die Cutting Cost", formatPKR(breakdown.dieCuttingCost))}
                  {renderDetail("Pasting Cost", formatPKR(breakdown.pastingCost))}
                  {renderDetail("Lamination Cost", formatPKR(breakdown.laminationCost + breakdown.laminationWastageCost + breakdown.insideLaminationCost + breakdown.insideLaminationWastageCost))}
                  {renderDetail("Additional Features", formatPKR(breakdown.additionalFeaturesCost), true)}
                </div>
              </div>
            )}

            <div className="bg-muted rounded-md border border-border border-l-4 border-l-primary">
              <div className="p-3 pb-2">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-1 bg-primary rounded-full"></div>
                  <h3 className="text-sm font-medium text-foreground">Shipping & Total</h3>
                </div>
              </div>
              <div className="px-3 pb-3 space-y-1">
                {canViewFullBreakdown ? (
                  <>
                    {renderDetail("Base Production Cost", formatPKR(productionCost))}
                    {effectiveProfits.manufacturerAmount > 0 && renderDetail(
                      `Manufacturer Profit (${effectiveProfits.manufacturerProfit}%)`,
                      formatPKR(isAgent ? (currentVendorProfitAmount ?? 0) : effectiveProfits.manufacturerAmount)
                    )}
                    {isAgent
                      ? renderDetail(
                          `Company Profit (Agent) (${agentMarginPercent}%)`,
                          formatPKR(agentCompanyProfit)
                        )
                      : (effectiveProfits.agentMargin > 0)
                        ? renderDetail(
                            `Company Profit (Agent) (${effectiveProfits.agentMargin}%)`,
                            formatPKR(agentMarginAmountDisplay ?? (baseTotalForAgent * (effectiveProfits.agentMargin / 100)))
                          )
                        : null}
                    {/* Show Company Margin for admin quotes with margin data, otherwise show Shipping Boxes */}
                    {effectiveProfits.companyMarginPercent > 0 ?
                      renderDetail(
                        `Company Margin (${effectiveProfits.companyMarginPercent}%)`,
                        formatPKR(effectiveProfits.companyMarginAmount)
                      ) :
                      (!isAgent && renderDetail("Shipping Boxes", breakdown.numberOfShippingBoxes ?? 'N/A'))
                    }
                    {renderDetail(
                      <div className="flex flex-col">
                        <span>Shipping Cost</span>
                        {breakdown.shippingMethod && (
                          <span className="text-xs text-muted-foreground">
                            {breakdown.shippingMethod.carrierName || breakdown.shippingMethod.carrier} - {breakdown.shippingMethod.destination}
                            {breakdown.shippingMethod.type === 'airFreight' && ' (Air Freight)'}
                            {breakdown.shippingMethod.type === 'seaFreight' && ' (Sea Freight)'}
                          </span>
                        )}
                      </div>, 
                      formatPKR(breakdown.shippingCost)
                    )}
                  </>
                ) : (
                  <div className="text-sm text-muted-foreground text-center py-2">
                    <div className="text-xs mb-2">Breakdown details restricted</div>
                  </div>
                )}
                <div className="flex justify-between items-center py-1.5 text-sm font-medium border-t border-border mt-2 pt-2">
                  <span className="text-xs text-foreground/80">Total Cost</span>
                  <span className="text-xs font-semibold text-foreground">{formatPKR(displayTotal)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Calculation Details Footer */}
        {breakdown.metadata && (
          <div className="px-4 py-3 border-t border-border bg-muted">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs font-medium text-foreground">Calculation Details</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-xs">
              {breakdown.metadata.pricingVersion && (
                <div className="flex flex-col">
                  <span className="text-muted-foreground mb-1">Pricing Version</span>
                  <div className="px-1.5 py-0.5 bg-background text-foreground rounded text-xs font-mono border border-border">
                    v{breakdown.metadata.pricingVersion}
                  </div>
                </div>
              )}
              {breakdown.metadata.processingTime && (
                <div className="flex flex-col">
                  <span className="text-muted-foreground mb-1">Processing Time</span>
                  <div className="px-1.5 py-0.5 bg-background text-foreground rounded text-xs border border-border">
                    {breakdown.metadata.processingTime}
                  </div>
                </div>
              )}
              {breakdown.metadata.calculatedAt && (
                <div className="flex flex-col">
                  <span className="text-muted-foreground mb-1">Calculated At</span>
                  <div className="px-1.5 py-0.5 bg-background text-foreground rounded text-xs border border-border">
                    {new Date(breakdown.metadata.calculatedAt).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
